import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { unitsAPI, formatApiErrorDetail } from '../../lib/api';
import { Plus, Buildings, MapPin, Users, PencilSimple } from '@phosphor-icons/react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const UNIT_TYPES = ['grand_priory', 'commandery', 'prefectory', 'committee', 'special_office'];
const STATUSES = ['active', 'inactive', 'pending'];

const UnitsPage = () => {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [formData, setFormData] = useState({
    name: '', type: 'commandery', parent_id: '', jurisdiction: '',
    region: '', status: 'active', head_member_id: '', description: ''
  });

  const fetchData = async () => {
    try {
      const response = await unitsAPI.getAll();
      setUnits(response.data);
    } catch (error) {
      console.error('Error fetching units:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredUnits = filterType === 'all' ? units : units.filter(u => u.type === filterType);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUnit) {
        await unitsAPI.update(editingUnit.id, formData);
        toast.success('Unit updated successfully');
      } else {
        await unitsAPI.create(formData);
        toast.success('Unit created successfully');
      }
      setShowModal(false);
      setEditingUnit(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleEdit = (unit) => {
    setEditingUnit(unit);
    setFormData({
      name: unit.name || '',
      type: unit.type || 'commandery',
      parent_id: unit.parent_id || '',
      jurisdiction: unit.jurisdiction || '',
      region: unit.region || '',
      status: unit.status || 'active',
      head_member_id: unit.head_member_id || '',
      description: unit.description || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '', type: 'commandery', parent_id: '', jurisdiction: '',
      region: '', status: 'active', head_member_id: '', description: ''
    });
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'grand_priory': return '🏰';
      case 'commandery': return '⚔️';
      case 'prefectory': return '🛡️';
      case 'committee': return '📋';
      default: return '🏛️';
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="units-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl text-[#1A1614]">Organizational Units</h1>
            <p className="text-[#8A847A]">{units.length} total units</p>
          </div>
          <Button 
            onClick={() => { resetForm(); setEditingUnit(null); setShowModal(true); }}
            className="bg-[#7B1818] hover:bg-[#5C1212] rounded-none"
            data-testid="add-unit-button"
          >
            <Plus size={18} className="mr-2" />
            Add Unit
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-4 bg-white border border-[#D8D2C4] p-4">
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-48 rounded-none" data-testid="filter-type">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {UNIT_TYPES.map(t => <SelectItem key={t} value={t} className="capitalize">{t.replace('_', ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Units Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="units-grid">
            {filteredUnits.map(unit => (
              <div 
                key={unit.id}
                className="bg-white border border-[#D8D2C4] p-6 hover:border-[#C5A059] transition-colors"
                data-testid={`unit-card-${unit.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#F5F2EB] flex items-center justify-center text-2xl">
                      {getTypeIcon(unit.type)}
                    </div>
                    <div>
                      <h3 className="font-heading text-lg text-[#1A1614]">{unit.name}</h3>
                      <p className="text-xs text-[#8A847A] capitalize">{unit.type?.replace('_', ' ')}</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => handleEdit(unit)}
                    className="p-2 hover:bg-[#F5F2EB] text-[#4A453E]"
                    data-testid={`edit-unit-${unit.id}`}
                  >
                    <PencilSimple size={18} />
                  </button>
                </div>

                {unit.description && (
                  <p className="text-sm text-[#4A453E] line-clamp-2 mb-4">{unit.description}</p>
                )}

                <div className="space-y-2 text-sm">
                  {unit.region && (
                    <div className="flex items-center gap-2 text-[#8A847A]">
                      <MapPin size={14} />
                      <span>{unit.region}</span>
                    </div>
                  )}
                  {unit.jurisdiction && (
                    <div className="flex items-center gap-2 text-[#8A847A]">
                      <Buildings size={14} />
                      <span>{unit.jurisdiction}</span>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-[#E8E4D9] flex justify-between items-center">
                  <span className={`px-2 py-1 text-xs capitalize ${
                    unit.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                  }`}>
                    {unit.status}
                  </span>
                  <div className="flex items-center gap-1 text-[#8A847A] text-xs">
                    <Users size={14} />
                    <span>{unit.members?.length || 0} members</span>
                  </div>
                </div>
              </div>
            ))}
            {filteredUnits.length === 0 && (
              <div className="col-span-full text-center py-12 text-[#8A847A]">
                No units found
              </div>
            )}
          </div>
        )}

        {/* Add/Edit Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">
                {editingUnit ? 'Edit Unit' : 'Add New Unit'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="unit-form">
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  className="rounded-none"
                  data-testid="unit-input-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Type</label>
                  <Select value={formData.type} onValueChange={(v) => setFormData({...formData, type: v})}>
                    <SelectTrigger className="rounded-none">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {UNIT_TYPES.map(t => <SelectItem key={t} value={t} className="capitalize">{t.replace('_', ' ')}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Status</label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                    <SelectTrigger className="rounded-none">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Region</label>
                  <Input
                    value={formData.region}
                    onChange={(e) => setFormData({...formData, region: e.target.value})}
                    className="rounded-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Jurisdiction</label>
                  <Input
                    value={formData.jurisdiction}
                    onChange={(e) => setFormData({...formData, jurisdiction: e.target.value})}
                    className="rounded-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Parent Unit</label>
                <Select value={formData.parent_id} onValueChange={(v) => setFormData({...formData, parent_id: v})}>
                  <SelectTrigger className="rounded-none">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">None</SelectItem>
                    {units.filter(u => u.id !== editingUnit?.id).map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                  className="rounded-none"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="rounded-none">
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#7B1818] hover:bg-[#5C1212] rounded-none" data-testid="unit-submit-button">
                  {editingUnit ? 'Update Unit' : 'Create Unit'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default UnitsPage;
